<template>
  <div class="p-8 max-w-[1000px] mx-auto space-y-8 animate-in fade-in duration-500">
    <!-- Header -->
    <div class="flex flex-col md:flex-row items-center justify-between border-b border-border pb-6 gap-4">
      <div class="space-y-1 text-center md:text-left">
        <h1 class="text-3xl font-black tracking-tight text-foreground">
          {{ isEdit ? 'Modifier l\'investissement' : 'Nouvel Investissement' }}
        </h1>
        <p class="text-muted-foreground font-medium italic">Configurez les paramètres de votre actif financier.</p>
      </div>
      <Button variant="ghost" size="sm" class="text-muted-foreground font-bold hover:text-foreground" @click="router.back()">
        <ArrowLeft class="w-4 h-4 mr-2" /> Retour
      </Button>
    </div>

    <form @submit.prevent="saveInvestment" class="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <!-- Left Column: Basic Info -->
      <div class="lg:col-span-7 space-y-6">
        <Card class="border-border shadow-sm bg-card">
          <CardHeader class="pb-4">
            <CardTitle class="text-lg font-bold flex items-center gap-2 text-foreground">
              <Info class="w-4 h-4 text-muted-foreground" /> Informations Générales
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-4">
            <div class="space-y-2">
              <label for="investment-name" class="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-1">Nom de l'actif</label>
              <input type="text" id="investment-name" v-model="form.name" required placeholder="ex. S&P 500 ETF, Immobilier..." 
                     class="w-full bg-muted/30 border border-border rounded-xl p-2.5 text-sm font-bold text-foreground focus:ring-2 focus:ring-primary/10 transition-all outline-none focus-visible:ring-indigo-500" />
            </div>

            <div class="space-y-2">
              <label for="investment-desc" class="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-1">Description courte (Aperçu)</label>
              <textarea id="investment-desc" v-model="form.description" rows="2" placeholder="Détails additionnels courts..." 
                        class="w-full bg-muted/30 border border-border rounded-xl p-2.5 text-sm font-medium text-foreground focus:ring-2 focus:ring-primary/10 transition-all outline-none resize-none focus-visible:ring-indigo-500"></textarea>
            </div>

            <div class="space-y-2">
              <label for="investment-category" class="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-1">Catégorie d'investissement</label>
              <div class="flex gap-2 text-primary">
                <select id="investment-category" v-model="form.categoryId" 
                        class="flex-1 bg-muted/30 border border-border rounded-xl p-2.5 text-sm font-bold text-foreground focus:ring-2 focus:ring-primary/10 transition-all outline-none focus-visible:ring-indigo-500">
                  <option :value="null" class="bg-card">-- Sélectionner une catégorie --</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id" class="bg-card">
                    {{ cat.displayName }}
                  </option>
                </select>
                <Button type="button" variant="outline" size="icon" class="h-10 w-10 shrink-0 bg-muted/30 border-border text-muted-foreground hover:text-foreground focus-visible:ring-indigo-500" 
                        @click="showCategoryModal = true" aria-label="Nouvelle catégorie">
                  <Plus class="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card class="border-border shadow-sm bg-card">
          <CardHeader class="pb-4">
            <CardTitle class="text-lg font-bold flex items-center gap-2 text-foreground">
              <Link2 class="w-4 h-4 text-muted-foreground" /> Lien & Notes privées
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-4">
            <div class="space-y-2">
              <label for="investment-url" class="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-1">URL (Site web)</label>
              <input type="url" id="investment-url" v-model="form.url" placeholder="https://..." 
                     class="w-full bg-muted/30 border border-border rounded-xl p-2.5 text-sm font-medium text-foreground focus:ring-2 focus:ring-primary/10 transition-all outline-none focus-visible:ring-indigo-500" />
            </div>
            <div class="space-y-2">
              <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-1">Commentaire Stratégique</label>
              <textarea v-model="form.comment" rows="6" placeholder="Vos notes stratégiques concernant ce placement..." 
                        class="w-full bg-muted/30 border border-border rounded-xl p-2.5 text-sm font-medium text-foreground focus:ring-2 focus:ring-primary/10 transition-all outline-none"></textarea>
            </div>
          </CardContent>
        </Card>
      </div>

      <!-- Right Column: Returns & Settings -->
      <div class="lg:col-span-5 space-y-6">
        <Card class="border-border shadow-sm bg-zinc-900 text-white overflow-hidden relative border-none">
          <div class="absolute right-[-20px] bottom-[-20px] opacity-10">
            <TrendingUp class="w-32 h-32 text-emerald-400" />
          </div>
          <CardHeader class="pb-4 relative z-10">
            <CardTitle class="text-lg font-bold flex items-center gap-2">
              Performance Espérée
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-6 relative z-10">
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <label class="text-[10px] font-black uppercase tracking-widest text-zinc-400">Taux Annuel (%)</label>
                <div class="flex items-center gap-2 bg-zinc-800 rounded-lg px-3 py-1 border border-zinc-700 shadow-inner">
                  <input type="number" step="0.01" min="0" max="100" v-model="form.expectedReturnRate" placeholder="5.00" 
                         class="bg-transparent border-none text-right w-16 text-sm font-black focus:outline-none tabular-nums text-white" />
                  <span class="text-xs font-black text-zinc-600">%</span>
                </div>
              </div>
              <p class="text-[10px] text-zinc-500 italic leading-relaxed">
                Ce taux simule la croissance brute sur le graphique d'évolution, s'additionnant à vos flux périodiques.
              </p>
            </div>
            <div class="pt-4 border-t border-zinc-800 space-y-3">
              <div class="flex items-center justify-between">
                <label class="text-[10px] font-black uppercase tracking-widest text-zinc-400">Date de sortie prévue</label>
                <input type="date" v-model="form.endDate" 
                     class="bg-zinc-800 border-none rounded-lg p-2 text-xs font-bold text-white focus:ring-2 focus:ring-primary transition-all outline-none" />
              </div>
              <p class="text-[10px] text-zinc-500 italic leading-relaxed">
                Si renseignée, un flux de sortie automatique égal à la valorisation courante sera simulé à cette date.
              </p>
            </div>
          </CardContent>
        </Card>
        <Card class="border-border shadow-sm overflow-hidden bg-card">
          <CardHeader class="bg-muted/30 pb-4">
            <CardTitle class="text-lg font-bold flex items-center gap-2 text-foreground">
              <Settings class="w-4 h-4 text-muted-foreground" /> Paramètres d'Affichage
            </CardTitle>
          </CardHeader>
          <CardContent class="p-0">
            <div class="divide-y divide-border">
              <label class="flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors cursor-pointer group">
                <div class="relative flex items-center">
                  <input type="checkbox" v-model="form.ignoreInGlobalSums" 
                         class="peer h-5 w-5 cursor-pointer appearance-none rounded border transition-all shadow-sm"
                         :class="form.ignoreInGlobalSums ? 'bg-primary border-primary' : 'bg-muted border-border'" />
                  <Check class="absolute left-0.5 top-0.5 w-4 h-4 text-primary-foreground opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
                </div>
                <div class="flex flex-col">
                  <span class="text-xs font-bold text-foreground">Exclure du patrimoine global</span>
                  <span class="text-[9px] text-muted-foreground font-medium uppercase tracking-tighter">L'actif ne comptera pas dans les totaux.</span>
                </div>
              </label>
              <label class="flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors cursor-pointer group">
                <div class="relative flex items-center">
                  <input type="checkbox" v-model="form.ignorePnL" 
                         class="peer h-5 w-5 cursor-pointer appearance-none rounded border transition-all shadow-sm"
                         :class="form.ignorePnL ? 'bg-primary border-primary' : 'bg-muted border-border'" />
                  <Check class="absolute left-0.5 top-0.5 w-4 h-4 text-primary-foreground opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
                </div>
                <div class="flex flex-col">
                  <span class="text-xs font-bold text-foreground">Ignorer la performance (P&L)</span>
                  <span class="text-[9px] text-muted-foreground font-medium uppercase tracking-tighter">Masque la plus-value relative.</span>
                </div>
              </label>
              <label class="flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors cursor-pointer group">
                <div class="relative flex items-center">
                  <input type="checkbox" v-model="form.isSimulation" 
                         class="peer h-5 w-5 cursor-pointer appearance-none rounded border transition-all shadow-sm"
                         :class="form.isSimulation ? 'bg-purple-500 border-purple-500' : 'bg-muted border-border'" />
                  <Check class="absolute left-0.5 top-0.5 w-4 h-4 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
                </div>
                <div class="flex flex-col">
                  <span class="text-xs font-bold text-purple-400">Mode Simulation</span>
                  <span class="text-[9px] text-muted-foreground font-medium uppercase tracking-tighter">Simule l'actif (exclu des totaux et graphiques globaux).</span>
                </div>
              </label>
            </div>
          </CardContent>
        </Card>

        <div class="flex flex-col gap-3">
          <Button type="submit" variant="finance" size="lg" shadow="xl" class="w-full h-14 text-base font-black uppercase tracking-widest shadow-xl shadow-black/20" 
                  :disabled="saving" :aria-busy="saving">
            <Save v-if="!saving" class="w-5 h-5 mr-3" aria-hidden="true" />
            <RefreshCw v-else class="w-5 h-5 mr-3 animate-spin" aria-hidden="true" />
            {{ saving ? 'Enregistrement...' : 'Valider Placement' }}
          </Button>
          <div v-if="error" class="text-xs font-bold text-red-400 text-center bg-red-500/10 p-3 rounded-xl border border-red-500/20 italic flex items-center justify-center gap-2">
            <Info class="w-4 h-4" /> {{ error }}
          </div>
        </div>
      </div>
    </form>

    <!-- Modal for new category -->
    <div v-if="showCategoryModal" 
         class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-sm animate-in fade-in duration-300"
         role="dialog" aria-modal="true" aria-labelledby="form-cat-modal-title"
         @keydown.esc="showCategoryModal = false">
      <Card class="w-full max-w-md shadow-2xl border border-border animate-in zoom-in-95 self-center bg-card overflow-hidden">
        <CardHeader class="border-b border-border pb-6 relative">
          <CardTitle id="form-cat-modal-title" class="text-xl font-black tracking-tight text-foreground">Nouvelle Catégorie</CardTitle>
          <CardDescription class="font-medium italic text-muted-foreground">Classez vos investissements par thématique.</CardDescription>
          <Button variant="ghost" size="icon" class="absolute right-4 top-4 h-8 w-8 text-muted-foreground hover:text-foreground" @click="showCategoryModal = false" aria-label="Fermer">
            <X class="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent class="py-6 space-y-4">
          <div class="space-y-2">
            <label for="new-cat-name" class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Nom de la catégorie</label>
            <input type="text" id="new-cat-name" v-model="newCategoryName" placeholder="ex. Cryptomonnaies, SCPI..." ref="catInput"
                   class="w-full bg-muted/30 border border-border rounded-xl p-3 text-sm font-bold focus:ring-2 focus:ring-primary/10 outline-none transition-all shadow-sm text-foreground focus-visible:ring-indigo-500" />
          </div>
          <div class="space-y-2">
            <label class="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Emplacement (Optionnel)</label>
            <select v-model="newCategoryParentId" 
                    class="w-full bg-muted/30 border border-border rounded-xl p-3 text-sm font-bold focus:ring-2 focus:ring-primary/10 outline-none transition-all shadow-sm text-foreground">
              <option :value="null" class="bg-card">-- Racine du menu --</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id" class="bg-card">{{ cat.displayName }}</option>
            </select>
          </div>
        </CardContent>
        <CardFooter class="flex justify-end gap-3 bg-muted/30 p-6 border-t border-border">
          <Button variant="ghost" class="font-bold text-muted-foreground hover:bg-muted" @click="showCategoryModal = false">Annuler</Button>
          <Button variant="finance" :disabled="!newCategoryName || creatingCat" @click="createCategory">
            {{ creatingCat ? 'Création...' : 'Créer Catégorie' }}
          </Button>
        </CardFooter>
      </Card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { 
  ArrowLeft, Info, Plus, Link2, X,
  Settings, Save, RefreshCw, Check, TrendingUp 
} from 'lucide-vue-next'

// UI Components
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent, CardFooter, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { slugify } from '@/lib/utils'
import { useGlobalState } from '../../composables/useGlobalState'

const router = useRouter()
const route = useRoute()
const { getAuthHeader } = useGlobalState()
const isEdit = !!route.params.id

const rawCategories = ref([])
const categories = computed(() => {
    const buildTree = (list, parentId = null, depth = 0) => {
        let tree = []
        list.filter(c => c.parentId === parentId).forEach(c => {
            tree.push({ ...c, displayName: '\u00A0\u00A0\u00A0'.repeat(depth) + (depth > 0 ? '└ ' : '') + c.name })
            tree = tree.concat(buildTree(list, c.id, depth + 1))
        })
        return tree
    }
    return buildTree(rawCategories.value)
})
const form = ref({ 
    name: '', 
    description: '', 
    comment: '', 
    url: '', 
    categoryId: null, 
    expectedReturnRate: '',
    ignoreInGlobalSums: false,
    ignorePnL: false,
    isSimulation: false,
    endDate: null,
})
const saving = ref(false)
const error = ref('')

// Category Modal state
const showCategoryModal = ref(false)
const newCategoryName = ref('')
const newCategoryParentId = ref(null)
const creatingCat = ref(false)
const catInput = ref(null)

watch(showCategoryModal, (v) => {
  if (v) {
    nextTick(() => {
        if (catInput.value) catInput.value.focus()
    })
  }
})

const fetchCategories = async () => {
    try {
        const res = await fetch('/api/investments/categories', {
            headers: getAuthHeader()
        })
        if (res.ok) rawCategories.value = await res.json()
    } catch (err) {
        console.error('Failed to load categories', err)
    }
}

const fetchInvestment = async () => {
    if (!isEdit) return
    try {
        const res = await fetch(`/api/investments/${route.params.id}`, {
            headers: getAuthHeader()
        })
        if (res.ok) {
            const data = await res.json()
            form.value = {
                name: data.name,
                description: data.description,
                comment: data.comment || '',
                url: data.url || '',
                categoryId: data.category ? data.category.id : null,
                expectedReturnRate: data.expectedReturnRate !== null ? parseFloat(data.expectedReturnRate) : null,
                ignoreInGlobalSums: !!data.ignoreInGlobalSums,
                ignorePnL: !!data.ignorePnL,
                isSimulation: !!data.isSimulation,
                endDate: data.endDate || null,
            }
        }
    } catch (err) {
        console.error(err)
    }
}

const saveInvestment = async () => {
    saving.value = true
    error.value = ''
    try {
        const url = isEdit ? `/api/investments/${route.params.id}` : '/api/investments'
        const method = isEdit ? 'PUT' : 'POST'
        
        const res = await fetch(url, {
            method,
            headers: { 
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify(form.value)
        })
        
        if (!res.ok) throw new Error('Échec de la sauvegarde')
        
        const data = await res.json()
        const targetId = isEdit ? route.params.id : data.id
        router.push({ 
            name: 'finance_investment_detail', 
            params: { id: targetId, name: slugify(form.value.name) } 
        })
    } catch (err) {
        error.value = err.message
    } finally {
        saving.value = false
    }
}

const createCategory = async () => {
    creatingCat.value = true
    try {
        const res = await fetch('/api/investments/categories', {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify({ name: newCategoryName.value, parentId: newCategoryParentId.value })
        })
        if (res.ok) {
            const newCat = await res.json()
            rawCategories.value.push(newCat)
            form.value.categoryId = newCat.id
            showCategoryModal.value = false
            newCategoryName.value = ''
            newCategoryParentId.value = null
        }
    } catch (err) {
        console.error(err)
    } finally {
        creatingCat.value = false
    }
}

onMounted(() => {
    fetchCategories()
    fetchInvestment()
})
</script>

<style scoped>
.tabular-nums {
  font-variant-numeric: tabular-nums;
}
</style>
